/**
 * M8 Financial REST client.
 *
 * Mirrors `/api/v1/financial/...` and `/api/v1/reports/...`. Reuses the
 * auth-store access token + ApiError envelope from the auth feature.
 */

import { env } from '@/lib/env';
import { ApiError, type ProblemDetail } from '@/features/auth/api';
import { useAuthStore } from '@/stores/auth-store';

// ── Types ────────────────────────────────────────────────────────────────
export type Allowance = {
  label: string;
  amount_minor: number;
  taxable?: boolean;
};

export type SalaryStructure = {
  id: string;
  field_user_id: string;
  base_minor: number;
  currency: string;
  allowances: Allowance[];
  effective_from: string;
  effective_to: string | null;
  formula_id: string | null;
  notes: string | null;
};

export type SalaryFormula = {
  id: string;
  slug: string;
  name: string;
  version: number;
  is_active: boolean;
  formula_text: string;
  variables_used: string[];
  description: string | null;
};

export type FormulaTraceStep = { label: string; value: string };
export type FormulaPreview = {
  value: string;
  trace: FormulaTraceStep[];
  variables_used: string[];
};

export type CommissionTier = {
  from: number;
  to: number | null;
  amount_minor?: number | null;
  percent?: number | null;
};

export type CommissionRule = {
  id: string;
  slug: string;
  name: string;
  is_enabled: boolean;
  priority: number;
  scope: 'ENGAGEMENT' | 'ASSET_TYPE' | 'FIELD_USER' | 'TENANT';
  scope_ref_id: string | null;
  flat_amount_minor: number | null;
  flat_percent: number | null;
  tiers: CommissionTier[];
  applies_when: Record<string, unknown>;
  currency: string;
  effective_from: string;
  effective_to: string | null;
};

export type CommissionAccrual = {
  id: string;
  field_user_id: string;
  rule_id: string;
  work_unit_id: string | null;
  engagement_id: string | null;
  amount_minor: number;
  currency: string;
  accrued_at: string;
  status: 'ACCRUED' | 'SETTLED' | 'CLAWED_BACK' | 'VOIDED';
  settled_at: string | null;
  payroll_run_id: string | null;
  note: string | null;
};

export type Fine = {
  id: string;
  field_user_id: string;
  amount_minor: number;
  currency: string;
  reason: string;
  issued_on: string;
  issued_by_user_id: string | null;
  status: 'ISSUED' | 'DEDUCTED' | 'WAIVED';
  evidence_path: string | null;
  deducted_in_payroll_id: string | null;
  waived_at: string | null;
  waiver_note: string | null;
};

export type PayrollRun = {
  id: string;
  period: string;
  period_start: string;
  period_end: string;
  currency: string;
  status: 'DRAFT' | 'FINALIZED' | 'CANCELLED';
  finalized_at: string | null;
  finalized_by_user_id: string | null;
  total_gross_minor: number;
  total_deductions_minor: number;
  total_net_minor: number;
  employee_count: number;
  notes: string | null;
};

export type PayrollPreviewRow = {
  field_user_id: string;
  employee_id: string;
  full_name: string;
  variables: Record<string, string>;
  trace: FormulaTraceStep[];
  formula_text: string;
  formula_version: number | null;
  base_minor: number;
  allowances_minor: number;
  overtime_minor: number;
  commission_minor: number;
  advances_minor: number;
  fines_minor: number;
  gross_minor: number;
  deductions_minor: number;
  net_minor: number;
  error: string | null;
};

export type PayrollPreview = {
  run_id: string;
  period: string;
  status: string;
  currency: string;
  employee_count: number;
  total_gross_minor: number;
  total_deductions_minor: number;
  total_net_minor: number;
  rows: PayrollPreviewRow[];
};

export type Payslip = {
  id: string;
  payroll_run_id: string;
  field_user_id: string;
  period: string;
  base_minor: number;
  allowances_minor: number;
  overtime_minor: number;
  commission_minor: number;
  gross_minor: number;
  advances_deducted_minor: number;
  fines_deducted_minor: number;
  deductions_minor: number;
  net_minor: number;
  currency: string;
  pdf_object_path: string | null;
  formula_snapshot: Record<string, unknown>;
};

export type Reconciliation = {
  id: string;
  fuel_log_id: string | null;
  expense_entry_id: string | null;
  engagement_id: string | null;
  asset_id: string | null;
  payroll_period: string | null;
  status: 'PENDING' | 'RECONCILED' | 'WRITTEN_OFF' | 'DISPUTED';
  resolved_at: string | null;
  resolved_by_user_id: string | null;
  note: string | null;
};

export type ReportRun = {
  id: string;
  name: string;
  format: 'CSV' | 'PDF' | 'XLSX';
  params: Record<string, unknown>;
  status: 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED';
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  output_object_path: string | null;
  row_count: number | null;
  byte_size: number | null;
  requested_by_user_id: string | null;
  created_at: string;
};

// ── HTTP helper (mirrors operations/api.ts) ─────────────────────────────
async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = useAuthStore.getState().accessToken;
  const url = `${env.VITE_API_URL}/api/v1${path}`;
  const headers = new Headers(init.headers ?? {});
  headers.set('Accept', 'application/json');
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(url, { ...init, headers });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const problem: ProblemDetail = (data?.error ?? data) || {
      detail: `HTTP ${res.status}`,
    };
    throw new ApiError(res.status, problem);
  }
  return data as T;
}

async function downloadBlob(path: string): Promise<Blob> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`${env.VITE_API_URL}/api/v1${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new ApiError(res.status, { detail: `HTTP ${res.status}` });
  }
  return res.blob();
}

// ── Endpoints ────────────────────────────────────────────────────────────
export const financialApi = {
  // Salary structures
  listStructures: (fieldUserId: string) =>
    call<SalaryStructure[]>(`/financial/salary-structures/by-user/${fieldUserId}`),
  createStructure: (body: Omit<SalaryStructure, 'id'>) =>
    call<SalaryStructure>('/financial/salary-structures', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateStructure: (id: string, body: Partial<SalaryStructure>) =>
    call<SalaryStructure>(`/financial/salary-structures/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  // Salary formulas
  listFormulas: () => call<SalaryFormula[]>('/financial/salary-formulas'),
  reviseFormula: (body: {
    slug: string;
    name: string;
    formula_text: string;
    description?: string | null;
  }) =>
    call<SalaryFormula>('/financial/salary-formulas', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  previewFormula: (formula_text: string, variables: Record<string, unknown>) =>
    call<FormulaPreview>('/financial/salary-formulas:preview', {
      method: 'POST',
      body: JSON.stringify({ formula_text, variables }),
    }),

  // Commission
  listCommissionRules: () => call<CommissionRule[]>('/financial/commission-rules'),
  createCommissionRule: (body: Omit<CommissionRule, 'id'>) =>
    call<CommissionRule>('/financial/commission-rules', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateCommissionRule: (id: string, body: Partial<CommissionRule>) =>
    call<CommissionRule>(`/financial/commission-rules/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  listAccruals: (fieldUserId: string, start: string, end: string) =>
    call<CommissionAccrual[]>(
      `/financial/commission-accruals/by-user/${fieldUserId}?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
    ),

  // Fines
  listFines: (fieldUserId: string) => call<Fine[]>(`/financial/fines/by-user/${fieldUserId}`),
  issueFine: (body: {
    field_user_id: string;
    amount_minor: number;
    currency?: string;
    reason: string;
    issued_on?: string;
  }) => call<Fine>('/financial/fines', { method: 'POST', body: JSON.stringify(body) }),
  waiveFine: (id: string, note?: string) =>
    call<Fine>(`/financial/fines/${id}:waive`, {
      method: 'POST',
      body: JSON.stringify({ note: note ?? null }),
    }),

  // Payroll
  listRuns: (limit = 24) => call<PayrollRun[]>(`/financial/payroll-runs?limit=${limit}`),
  openRun: (period: string, currency = 'AED') =>
    call<PayrollRun>('/financial/payroll-runs', {
      method: 'POST',
      body: JSON.stringify({ period, currency }),
    }),
  previewRun: (runId: string) => call<PayrollPreview>(`/financial/payroll-runs/${runId}/preview`),
  finalizeRun: (runId: string) =>
    call<PayrollRun>(`/financial/payroll-runs/${runId}:finalize`, { method: 'POST' }),
  cancelRun: (runId: string) =>
    call<PayrollRun>(`/financial/payroll-runs/${runId}:cancel`, { method: 'POST' }),
  listPayslipsForRun: (runId: string) =>
    call<Payslip[]>(`/financial/payroll-runs/${runId}/payslips`),
  downloadWpsCsv: (runId: string) => downloadBlob(`/financial/payroll-runs/${runId}/wps.csv`),
  listPayslipsForUser: (fieldUserId: string, limit = 24) =>
    call<Payslip[]>(`/financial/payslips/by-user/${fieldUserId}?limit=${limit}`),

  // Expense reconciliation
  listPendingReconciliations: (limit = 200) =>
    call<Reconciliation[]>(`/financial/reconciliations/pending?limit=${limit}`),
  assignReconciliation: (
    id: string,
    body: { engagement_id?: string; asset_id?: string; note?: string },
  ) =>
    call<Reconciliation>(`/financial/reconciliations/${id}:assign`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  writeOffReconciliation: (id: string, note: string) =>
    call<Reconciliation>(`/financial/reconciliations/${id}:write-off`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),
};

export const reportsApi = {
  catalog: () => call<string[]>('/reports/_catalog'),
  list: (params: { name?: string; status?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return call<ReportRun[]>(`/reports${qs ? `?${qs}` : ''}`);
  },
  start: (name: string, params: Record<string, unknown>, format: 'CSV' | 'PDF' | 'XLSX' = 'CSV') =>
    call<ReportRun>('/reports', {
      method: 'POST',
      body: JSON.stringify({ name, params, format }),
    }),
  // Synchronous execution endpoint is POST; a GET would fall through to
  // `GET /reports/{run_id}` and 422 on UUID parsing of "{id}:run-now".
  runNow: async (id: string): Promise<{ blob: Blob; rowCount: number }> => {
    const token = useAuthStore.getState().accessToken;
    const res = await fetch(`${env.VITE_API_URL}/api/v1/reports/${id}:run-now`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      let problem: ProblemDetail = { detail: `HTTP ${res.status}` };
      try {
        const text = await res.text();
        const data = text ? JSON.parse(text) : undefined;
        problem = (data?.error ?? data) || problem;
      } catch {
        /* non-JSON error body */
      }
      throw new ApiError(res.status, problem);
    }
    const rowCount = Number(res.headers.get('X-Row-Count') ?? '0');
    const blob = await res.blob();
    return { blob, rowCount };
  },
  get: (id: string) => call<ReportRun>(`/reports/${id}`),
};
